import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo_new.png';
import logoWhite from '../assets/logo_new_white.png';
import { Search, Globe, TrendingUp, MessagesSquare, ChevronRight, Lock } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Auto rotate tabs
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const modules = [
    {
      id: 0,
      title: 'GEO 引擎优化',
      desc: '拦截 AI 时代的提问流量，构建品牌心智占位',
      icon: <Globe className="w-6 h-6" />,
      color: 'from-blue-500 to-indigo-600',
      bgClass: 'bg-blue-500/10 text-blue-400',
      stats: [
        { label: '覆盖AI引擎', value: '8+' },
        { label: '品牌检索量', value: '1,200W+' }
      ]
    },
    {
      id: 1,
      title: 'SEO 搜索优化',
      desc: '构建稳固的自然搜索护城河，长效获取精准流量',
      icon: <Search className="w-6 h-6" />,
      color: 'from-emerald-400 to-teal-500',
      bgClass: 'bg-emerald-500/10 text-emerald-400',
      stats: [
        { label: '收录索引量', value: '5,000W+' },
        { label: '自然流量增幅', value: '320%' }
      ]
    },
    {
      id: 2,
      title: 'SEM 竞价策略',
      desc: '洞察竞品投放，精准 ROI 转化，让每一分预算都值得',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'from-orange-400 to-amber-500',
      bgClass: 'bg-orange-500/10 text-orange-400',
      stats: [
        { label: '线索转化率', value: '12.8%' },
        { label: '投放成本下降', value: '-35%' }
      ]
    },
    {
      id: 3,
      title: '信息流推荐',
      desc: '兴趣标签触达，主动出击获取高潜力增量客户',
      icon: <MessagesSquare className="w-6 h-6" />,
      color: 'from-pink-500 to-rose-500',
      bgClass: 'bg-pink-500/10 text-pink-400',
      stats: [
        { label: '曝光触达数', value: '1.5亿+' },
        { label: '兴趣人群圈层', value: '450+' }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex bg-[#060913] overflow-hidden relative font-sans text-slate-300">
      {/* Background Orbs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className={`absolute top-[-10%] left-[-10%] w-[50%] h-[60%] rounded-full opacity-20 blur-[120px] transition-all duration-1000 bg-gradient-to-br ${modules[activeTab].color}`}></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-slate-800/40 blur-[100px]"></div>
      </div>

      {/* Left Column: Platform Presentation */}
      <div className="hidden lg:flex lg:w-7/12 relative flex-col justify-center px-12 xl:px-24 z-10">
        <div className="w-full max-w-2xl">
          <div className="mb-12">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-6">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
              <span className="text-sm font-medium text-slate-300">一体化营销与搜索中台</span>
            </div>
            <h1 className="text-5xl xl:text-6xl font-extrabold text-white tracking-tight leading-[1.15] mb-6">
              思序 Sapient Rank <br/>
              <span className={`text-transparent bg-clip-text bg-gradient-to-r ${modules[activeTab].color} transition-colors duration-500`}>
                全域搜索与营销智能中台
              </span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed max-w-xl">
              深度整合 GEO、SEO、SEM 与信息流数据矩阵。从 AI 引擎心智占位到传统搜索全网霸屏，全链路洞察口碑与动态，用数据驱动转化。
            </p>
          </div>

          {/* Interactive Carousel Selector */}
          <div className="space-y-4 relative">
            {/* Connection Line */}
            <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-slate-800/50 -z-10 rounded-full"></div>

            {modules.map((mod, idx) => (
              <div 
                key={mod.id}
                onClick={() => setActiveTab(mod.id)}
                className={`relative flex items-center p-4 rounded-2xl cursor-pointer transition-all duration-300 border ${
                  activeTab === idx 
                    ? 'bg-slate-800/60 border-slate-700/50 shadow-lg scale-[1.02] transform' 
                    : 'bg-transparent border-transparent hover:bg-slate-800/30'
                }`}
              >
                <div className={`px-6 py-3 rounded-xl flex-shrink-0 transition-colors duration-300 ${activeTab === idx ? mod.bgClass : 'bg-slate-800 text-slate-500'}`}>
                  {mod.icon}
                </div>
                <div className="ml-5 flex-1">
                  <h3 className={`text-lg font-bold transition-colors duration-300 ${activeTab === idx ? 'text-white' : 'text-slate-400'}`}>
                    {mod.title}
                  </h3>
                  {activeTab === idx && (
                    <p className="text-sm text-slate-400 mt-1 animate-fadeIn">
                      {mod.desc}
                    </p>
                  )}
                </div>
                {activeTab === idx && (
                  <div className="hidden sm:flex space-x-4 animate-fadeIn">
                    {mod.stats.map((stat, i) => (
                      <div key={i} className="text-right">
                        <div className="text-xs text-slate-500">{stat.label}</div>
                        <div className="text-sm font-bold text-slate-200">{stat.value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column: Login Form */}
      <div className="w-full lg:w-5/12 flex flex-col justify-center items-center p-6 lg:p-12 relative z-10 border-l border-white/5 bg-[#0A0D18]/80 backdrop-blur-xl">
        <div className="w-full max-w-[420px]">
          <div className="text-center mb-10">
            <div className="inline-flex justify-center items-center bg-transparent px-6 py-3 rounded-2xl mb-6 hover:scale-105 transition-transform duration-300">
              <img src={logo} alt="Logo" className="h-9 md:h-11 w-auto object-contain dark:hidden" />
                <img src={logoWhite} alt="Logo" className="h-9 md:h-11 w-auto object-contain hidden dark:block" />
            </div>
            <h2 className="text-3xl font-bold text-white tracking-wide">统一工作台入口</h2>
            <p className="text-slate-500 mt-3 text-sm">一套账号，打通 GEO / SEO / SEM / Feeds 全链路生态</p>
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl mb-6 text-sm flex items-center">
              <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">账号邮箱</label>
              <div className="relative">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-5 pr-4 py-3.5 border border-slate-700/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-slate-800/50 text-white outline-none placeholder-slate-600"
                  placeholder="name@sapientrank.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">登录密码</label>
              <div className="relative">
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-5 pr-4 py-3.5 border border-slate-700/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-slate-800/50 text-white outline-none placeholder-slate-600"
                  placeholder="••••••••"
                />
                <Lock className="absolute right-4 top-4 w-5 h-5 text-slate-600" />
              </div>
            </div>

            <button 
              type="submit"
              disabled={loading}
              className={`w-full group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-4 px-4 rounded-xl transition-all font-bold text-base shadow-lg shadow-blue-500/25 mt-6 flex items-center justify-center ${
                loading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {loading ? '身份验证中...' : '进入控制台'}
              {!loading && <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-slate-800 text-center">
            <div className="flex justify-center space-x-3 sm:space-x-4 whitespace-nowrap text-sm text-slate-500 font-medium">
              <a href="https://www.sapientrank.com/geo" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer transition-colors">GEO 分析</a>
              <span className="w-1 h-1 rounded-full bg-slate-700 self-center"></span>
              <span className="hover:text-white cursor-pointer transition-colors">SEO 矩阵</span>
              <span className="w-1 h-1 rounded-full bg-slate-700 self-center"></span>
              <span className="hover:text-white cursor-pointer transition-colors">SEM 投放</span>
              <span className="w-1 h-1 rounded-full bg-slate-700 self-center"></span>
              <span className="hover:text-white cursor-pointer transition-colors">Feeds 投流</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="absolute bottom-5 right-5 lg:w-5/12 text-center text-slate-600 text-xs z-20 pointer-events-none">
        Copyright © 2026 Sapient Rank. | <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer" className="hover:text-slate-400 transition-colors pointer-events-auto">京ICP备2026014122号-2</a>
      </div>
    </div>
  );
}
